/**
 * CustomerAttachmentViewer
 *
 * In-dialog renderer for customer/prospect-scoped attachments. Picks a
 * viewer based on file type (image / PDF / video / audio / CSV / text /
 * Excel / Word) and falls back to a download prompt for anything else.
 *
 * Auth: every fetch is done via the bearer token in localStorage. The
 * image / video / audio branches convert the response to a blob: URL so the
 * JWT never appears in any URL the <img> / <video> / <audio> sees. PDFs are
 * fetched as raw bytes and painted with pdf.js → <canvas> (PdfReader),
 * NEVER a blob: <iframe> — the Tauri desktop shells have no native PDF plugin
 * for a blob:/data: frame. See the PDF branch + docs/crm.md §11 + memory
 * xop_edge_staff_pdf_iframe_hangs_wkwebview.
 *
 * Lifted out of `file-manager-panel.tsx` verbatim — file-type predicates
 * were replaced with shared helpers from `@/lib/file-type-utils`, but
 * rendering logic, side effects, and panel-specific UX (no PowerPoint
 * branch) are preserved exactly to avoid any behavior change.
 *
 * NOTE on the shared `@/components/file-manager/universal-file-viewer`:
 * that component targets the global `/api/v1/files/*` API and a different
 * `FileItem` shape; it cannot be reused here without changing behavior,
 * so this customer-attachment-specific viewer remains separate.
 */

import { useState, useEffect, useCallback } from "react"
import { Box, Button, CircularProgress, Typography } from "@mui/material"
import { Download, File, FileText, Music } from "lucide-react"
import { customerAttachmentsApi } from "@crm-src/lib/customer-attachments-api"
import { crmApiUrl } from "@crm-src/lib/api"
import { PdfReader } from "@/components/pdf/pdf-reader"
import {
  isAudioFile,
  isCsvFile,
  isDocFile,
  isDocxFile,
  isExcelFile,
  isImageFile,
  isPdfFile,
  isTextFile,
  isVideoFile,
  isWordFile,
} from "@crm-src/lib/file-type-utils"
import type { FileItem } from "../file-manager-panel"

// React document viewers — kept as runtime imports so callers that don't
// open a previewable document still avoid the cost of parsing them.
// @ts-ignore
import * as XLSX from "xlsx"
import mammoth from "mammoth"

export interface CustomerAttachmentViewerProps {
  file: FileItem
  customerId: string
  onError: (error: string) => void
  onDownload: () => void
}

export const CustomerAttachmentViewer: React.FC<CustomerAttachmentViewerProps> = ({
  file,
  customerId,
  onError,
  onDownload,
}) => {
  const [loading, setLoading] = useState(false)
  const [content, setContent] = useState<string | null>(null)
  const [csvData, setCsvData] = useState<string[][] | null>(null)
  const [excelData, setExcelData] = useState<any>(null)
  const [wordContent, setWordContent] = useState<string>('')
  // PDF bytes for the pdf.js → <canvas> renderer (PdfReader), NOT a
  // blob: <iframe>. See the PDF render branch below + docs/crm.md §11.
  const [pdfBytes, setPdfBytes] = useState<ArrayBuffer | null>(null)

  // Build the API URL WITHOUT the token in the query string. The auth token
  // is sent via the Authorization header in the fetch below, and the result
  // is converted to a blob URL for safe embedding in iframe/img/video/audio.
  const buildAttachmentUrl = (fileId: string) => {
    return crmApiUrl(`/api/v1/customers/${customerId}/attachments/${fileId}/view`)
  }

  const fetchAuthedAttachmentBlob = useCallback(async (fileId: string): Promise<string> => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('xop_crm_token') : null
    const resp = await fetch(buildAttachmentUrl(fileId), {
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    })
    if (!resp.ok) throw new Error(`Failed to load attachment (${resp.status})`)
    const blob = await resp.blob()
    return URL.createObjectURL(blob)
  }, [customerId])

  const [viewBlobUrl, setViewBlobUrl] = useState<string>('')

  // Load a viewable blob URL for the media branches (image / video / audio).
  // PDFs are NOT fetched here — they render via PdfReader from raw bytes
  // (see loadDocumentContent), so a blob URL would be fetched-but-unused.
  // Cleans up the previous blob URL when the file changes or unmounts.
  useEffect(() => {
    let active = true
    let createdUrl: string | null = null
    const load = async () => {
      if (isPdfFile(file)) return
      try {
        const url = await fetchAuthedAttachmentBlob(file.id)
        if (!active) {
          URL.revokeObjectURL(url)
          return
        }
        createdUrl = url
        setViewBlobUrl(url)
      } catch {
        if (active) onError('Failed to load file')
      }
    }
    load()
    return () => {
      active = false
      if (createdUrl) URL.revokeObjectURL(createdUrl)
    }
  }, [file.id, fetchAuthedAttachmentBlob, onError])

  // Load text content for text files and CSV via the authenticated API.
  const loadTextContent = useCallback(async () => {
    if (!isTextFile(file) && !isCsvFile(file)) return

    try {
      setLoading(true)
      const response = await customerAttachmentsApi.getFileContent(customerId, file.id)
      const text = response.data

      if (isCsvFile(file)) {
        // Parse CSV content
        const rows = text.split('\n').map((row: string) => row.split(',').map((cell: string) => cell.trim().replace(/^"/, '').replace(/"$/, '')))
        setCsvData(rows.filter((row: string[]) => row.some((cell: string) => cell.length > 0)))
      } else {
        setContent(text)
      }
    } catch (error) {
      console.error('Error loading text content:', error)
      onError('Failed to load file content')
    } finally {
      setLoading(false)
    }
  }, [file.id, customerId])

  // Load document content using React libraries (XLSX, mammoth).
  const loadDocumentContent = useCallback(async () => {
    try {
      setLoading(true)
      const docToken = typeof window !== 'undefined' ? localStorage.getItem('xop_crm_token') : null
      const response = await fetch(buildAttachmentUrl(file.id), {
        credentials: 'include',
        headers: docToken ? { Authorization: `Bearer ${docToken}` } : {}
      })

      if (!response.ok) {
        throw new Error('Failed to fetch file')
      }

      if (isExcelFile(file)) {
        const arrayBuffer = await response.arrayBuffer()
        try {
          const workbook = XLSX.read(arrayBuffer, { type: 'array' })
          const sheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[sheetName]
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 })

          setExcelData({
            rows: jsonData as any[][]
          })
        } catch (err) {
          console.error('Excel parse error:', err)
          onError('Failed to parse Excel file')
        }
      } else if (isDocxFile(file)) {
        const arrayBuffer = await response.arrayBuffer()
        const result = await mammoth.extractRawText({ arrayBuffer })
        setWordContent(result.value)
      } else if (isPdfFile(file)) {
        // Hand the raw bytes to PdfReader (pdf.js → <canvas>), never a
        // blob: <iframe>. PdfReader copies the buffer internally
        // (toPdfFileData) before pdf.js detaches it, so these source bytes
        // survive a re-render.
        const arrayBuffer = await response.arrayBuffer()
        setPdfBytes(arrayBuffer)
      }
    } catch (error) {
      console.error('Error loading document content:', error)
      onError('Failed to load file content')
    } finally {
      setLoading(false)
    }
  }, [file.id, file.name, customerId])

  useEffect(() => {
    if (isTextFile(file) || isCsvFile(file)) {
      loadTextContent()
    } else if (isExcelFile(file) || isDocxFile(file) || isPdfFile(file)) {
      loadDocumentContent()
    }
  }, [file.id, loadTextContent, loadDocumentContent])

  // Image Viewer - blob URL (no token in src URL).
  if (isImageFile(file)) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "surfaceSoft", borderRadius: 1, minHeight: 400 }}>
        {viewBlobUrl ? (
          <Box
            component="img"
            src={viewBlobUrl}
            alt={file.name}
            onError={() => onError('Failed to load image')}
            sx={{ maxWidth: "100%", maxHeight: 600, objectFit: "contain", borderRadius: 1, boxShadow: 3 }}
          />
        ) : (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>Loading...</Typography>
        )}
      </Box>
    )
  }

  // PDF Viewer — pdf.js → <canvas> via the shared PdfReader, NEVER a
  // blob: <iframe>. The Tauri desktop shells have no native PDF plugin for a
  // blob:/data: frame (WebView2 shows a broken-document placeholder, WKWebView
  // can spin/hang), so the old iframe — fine on the web — blanked the desktop
  // app. PdfReader paints each page to a <canvas> (identical web +
  // desktop), hands pdf.js a COPY of the bytes (toPdfFileData) so the "Buffer
  // is already detached" crash can't recur, and degrades to a Download-only
  // card on any render failure (the dialog footer keeps the non-hang Download).
  // Same renderer as the quote/invoice detail + AI-lead-brief previews. See
  // docs/crm.md §11 + memory xop_edge_staff_pdf_iframe_hangs_wkwebview.
  if (isPdfFile(file)) {
    if (loading || !pdfBytes) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 800, bgcolor: "surfaceSoft", borderRadius: 1, border: 1, borderColor: "divider" }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>Loading...</Typography>
        </Box>
      )
    }
    return (
      <PdfReader
        key={file.id}
        bytes={pdfBytes}
        filename={file.name}
        containerTestId="attachment-pdf-preview"
        fallbackTestId="attachment-pdf-fallback"
        fallbackText="This PDF couldn’t be previewed inline. Use Download to view it."
        maxHeight={800}
      />
    )
  }

  // Video Player
  if (isVideoFile(file)) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "common.black", borderRadius: 1 }}>
        {viewBlobUrl ? (
          <Box
            component="video"
            src={viewBlobUrl}
            controls
            onError={() => onError('Failed to load video')}
            sx={{ maxWidth: "100%", maxHeight: 600, borderRadius: 1 }}
          >
            Your browser does not support video playback.
          </Box>
        ) : (
          <Typography variant="body2" sx={{ color: "text.secondary", p: 4 }}>Loading...</Typography>
        )}
      </Box>
    )
  }

  // Audio Player
  if (isAudioFile(file)) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "surfaceSoft", borderRadius: 1, minHeight: 200 }}>
        <Box sx={{ textAlign: "center", display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: 80, height: 80, bgcolor: (theme) => theme.palette.mode === "light" ? "#dbeafe" : "#1e3a5f", borderRadius: "50%", mx: "auto", color: "primary.main" }}>
            <Music size={32} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 500, fontSize: "1.125rem" }}>{file.name}</Typography>
            {viewBlobUrl ? (
              <Box
                component="audio"
                src={viewBlobUrl}
                controls
                onError={() => onError('Failed to load audio')}
                sx={{ mt: 2 }}
              >
                Your browser does not support audio playback.
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>Loading...</Typography>
            )}
          </Box>
        </Box>
      </Box>
    )
  }

  // CSV Viewer
  if (isCsvFile(file)) {
    if (loading) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 6 }}>
          <CircularProgress size={32} sx={{ mr: 1 }} />
          <span>Loading CSV...</span>
        </Box>
      )
    }

    if (csvData && csvData.length > 0) {
      return (
        <Box sx={{ bgcolor: "background.paper", borderRadius: 1, border: 1, borderColor: "divider", overflow: "hidden" }}>
          <Box sx={{ overflow: "auto", maxHeight: 600 }}>
            <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
              <Box component="thead" sx={{ bgcolor: "surfaceSoft", position: "sticky", top: 0 }}>
                <tr>
                  {csvData[0].map((header, index) => (
                    <Box
                      component="th"
                      key={index}
                      sx={{ border: 1, borderColor: "divider", px: 2, py: 1, textAlign: "left", fontSize: "0.875rem", fontWeight: 500, color: "text.primary" }}
                    >
                      {header}
                    </Box>
                  ))}
                </tr>
              </Box>
              <tbody>
                {csvData.slice(1).map((row, rowIndex) => (
                  <Box component="tr" key={rowIndex} sx={{ bgcolor: rowIndex % 2 === 0 ? "background.paper" : "surfaceSoft" }}>
                    {row.map((cell, cellIndex) => (
                      <Box
                        component="td"
                        key={cellIndex}
                        sx={{ border: 1, borderColor: "divider", px: 2, py: 1, fontSize: "0.875rem", color: "text.secondary" }}
                      >
                        {cell}
                      </Box>
                    ))}
                  </Box>
                ))}
              </tbody>
            </Box>
          </Box>
          <Box sx={{ px: 2, py: 1, bgcolor: "surfaceSoft", borderTop: 1, borderColor: "divider", fontSize: "0.875rem", color: "text.secondary" }}>
            {csvData.length - 1} rows, {csvData[0].length} columns
          </Box>
        </Box>
      )
    }
  }

  // Text File Viewer
  if (isTextFile(file)) {
    if (loading) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 6 }}>
          <CircularProgress size={32} sx={{ mr: 1 }} />
          <span>Loading text...</span>
        </Box>
      )
    }

    if (content !== null) {
      return (
        <Box sx={{ bgcolor: "surfaceSoft", borderRadius: 1, p: 2 }}>
          <Box sx={{ bgcolor: "background.paper", borderRadius: 1, border: 1, borderColor: "divider", p: 2, fontFamily: "monospace", fontSize: "0.875rem", overflow: "auto", maxHeight: 600, whiteSpace: "pre-wrap" }}>
            {content}
          </Box>
        </Box>
      )
    }
  }

  // Excel Viewer
  if (isExcelFile(file)) {
    if (loading) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 4 }}>
          <CircularProgress size={32} sx={{ color: "text.secondary" }} />
          <Typography sx={{ ml: 1, color: "text.secondary" }}>Loading Excel file...</Typography>
        </Box>
      )
    }

    if (!excelData || !excelData.rows) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 4 }}>
          <Typography sx={{ color: "text.secondary" }}>No Excel data to display</Typography>
        </Box>
      )
    }

    return (
      <Box sx={{ width: "100%", overflow: "auto", maxHeight: 700, bgcolor: "background.paper", borderRadius: 1, border: 1, borderColor: "divider" }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider", bgcolor: "surfaceSoft" }}>
          <Typography sx={{ fontWeight: 500 }}>Excel Spreadsheet: {file.name}</Typography>
        </Box>
        <Box component="table" sx={{ width: "100%", fontSize: "0.875rem" }}>
          <tbody>
            {excelData.rows.map((row: any[], rowIndex: number) => (
              <Box
                component="tr"
                key={rowIndex}
                sx={{
                  borderBottom: 1,
                  borderColor: "divider",
                  ...(rowIndex === 0
                    ? { bgcolor: "surfaceSoft", fontWeight: 500 }
                    : { "&:hover": { bgcolor: "surfaceSoft" } }),
                }}
              >
                {row.map((cell: any, cellIndex: number) => (
                  <Box component="td" key={cellIndex} sx={{ p: 1.5, color: "text.secondary", borderRight: 1, borderColor: "divider" }}>
                    {cell || ''}
                  </Box>
                ))}
              </Box>
            ))}
          </tbody>
        </Box>
      </Box>
    )
  }

  // Word Document Files - Handle both DOC and DOCX formats
  if (isWordFile(file)) {
    // Legacy DOC files (binary format) - can't be parsed well in browser
    if (isDocFile(file)) {
      return (
        <Box sx={{ width: "100%", height: 700, background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)", borderRadius: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Box sx={{ textAlign: "center", display: "flex", flexDirection: "column", gap: 2, p: 4, maxWidth: 512 }}>
            <Box sx={{ width: 96, height: 96, bgcolor: "background.paper", borderRadius: 1, boxShadow: 3, display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", color: "primary.main" }}>
              <FileText size={48} />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: "text.primary" }}>Legacy Word File (DOC)</Typography>
              <Typography sx={{ fontSize: "1rem", color: "text.secondary", lineHeight: 1.6 }}>
                This is an older Word format that can't be displayed in the browser.
                Please convert to DOCX format or download to view.
              </Typography>
            </Box>
          </Box>
        </Box>
      )
    }

    // DOCX files - can be processed with mammoth
    if (isDocxFile(file)) {
      if (loading) {
        return (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 4 }}>
            <CircularProgress size={32} sx={{ color: "text.secondary" }} />
            <Typography sx={{ ml: 1, color: "text.secondary" }}>Loading Word document...</Typography>
          </Box>
        )
      }

      return (
        <Box sx={{ width: "100%", height: 700, bgcolor: "background.paper", borderRadius: 1, border: 1, borderColor: "divider", p: 3, overflow: "auto" }}>
          <Box sx={{ mb: 2, pb: 2, borderBottom: 1, borderColor: "divider" }}>
            <Typography sx={{ fontWeight: 500, fontSize: "1.125rem" }}>Word Document: {file.name}</Typography>
          </Box>
          <Box sx={{ maxWidth: "none" }}>
            <Box component="pre" sx={{ whiteSpace: "pre-wrap", fontSize: "0.875rem", color: "text.primary", fontFamily: (theme) => theme.typography.fontFamily, lineHeight: 1.6 }}>
              {wordContent}
            </Box>
          </Box>
        </Box>
      )
    }
  }

  // Fallback for unsupported files - prompt the user to download.
  return (
    <Box sx={{ width: "100%", height: 400, bgcolor: "surfaceSoft", borderRadius: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Box sx={{ textAlign: "center", display: "flex", flexDirection: "column", gap: 3, p: 4, maxWidth: 512 }}>
        <Box sx={{ width: 96, height: 96, bgcolor: "background.paper", borderRadius: 1, boxShadow: 3, display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", color: "text.secondary" }}>
          <File size={48} />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Typography sx={{ fontSize: "1.25rem", fontWeight: 700, color: "text.primary" }}>{file.name}</Typography>
          <Typography sx={{ color: "text.secondary" }}>This file type cannot be previewed in the browser</Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Button onClick={onDownload} variant="contained" startIcon={<Download size={16} />}>
            Download File
          </Button>
        </Box>
      </Box>
    </Box>
  )
}
